import React from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col } from 'reactstrap';
import PropTypes from 'prop-types';
import { getFieldAccess } from '../../../../_helper/fieldAccess';
import { normalizeFieldValue, validateLicensePlate, displayDecimal } from '../../../../_helper/regexHandler';

const AccidentInfoForm = ({ formData, errors, setFormData, handleInputChange, fieldPermissions = [], canEdit = true }) => {

    const licensePlateAccess = getFieldAccess(fieldPermissions, 'licenseplates.licenseplatenumber', canEdit);
    const chassisNumberAccess = getFieldAccess(fieldPermissions, 'licenseplates.carchassisnumber', canEdit);
    const carBrandAccess = getFieldAccess(fieldPermissions, 'licenseplates.carbrand', canEdit);
    const carModelAccess = getFieldAccess(fieldPermissions, 'licenseplates.carmodel', canEdit);
    const carYearAccess = getFieldAccess(fieldPermissions, 'licenseplates.caryear', canEdit);
    const customerContractRateAccess = getFieldAccess(fieldPermissions, 'customers.contractrate', canEdit);
    const customerEnforcementShareAccess = getFieldAccess(fieldPermissions, 'customers.enforcementattorneyfeesharerate', canEdit);
    const customerJudgmentShareAccess = getFieldAccess(fieldPermissions, 'customers.judgmentattorneyfeesharerate', canEdit);
    const customerPayInterestAccess = getFieldAccess(fieldPermissions, 'customers.payinteresttocustomer', canEdit);
    const willLorAccess = getFieldAccess(fieldPermissions, 'accidents.willlorbeworked', canEdit);
    const lovClaimAccess = getFieldAccess(fieldPermissions, 'licenseplates.istherelovclaim', canEdit);
    const carSoldAccess = getFieldAccess(fieldPermissions, 'licenseplates.hascarsoldbefore', canEdit);
    const notaryFeeAccess = getFieldAccess(fieldPermissions, 'accidents.shouldpaynotaryfee', canEdit);
    const notaryAmountAccess = getFieldAccess(fieldPermissions, 'accidents.notaryfeeamount', canEdit);
    const estDepreciationAccess = getFieldAccess(fieldPermissions, 'accidents.estimateddepreciationamount', canEdit);
    const damageAmountAccess = getFieldAccess(fieldPermissions, 'accidents.damageamount', canEdit);
    const damageDiffAccess = getFieldAccess(fieldPermissions, 'accidents.damagedifferenceamount', canEdit);
    const lorAmountAccess = getFieldAccess(fieldPermissions, 'accidents.loramount', canEdit);
    const accidentDateAccess = getFieldAccess(fieldPermissions, 'accidents.accidentdate', canEdit);
    const ourFaultRateAccess = getFieldAccess(fieldPermissions, 'accidents.ourfaultrate', canEdit);
    const trialDocAccess = getFieldAccess(fieldPermissions, 'accidents.istrialdocument', canEdit);
    const workDocAccess = getFieldAccess(fieldPermissions, 'accidents.isworkdocument', canEdit);
    const lovClaimAmountAccess = getFieldAccess(fieldPermissions, 'accidents.lovclaimamount', canEdit);
    const lpImpeachmentStatusAccess = getFieldAccess(fieldPermissions, 'accidents.lpimpeachmentstatus', canEdit);
    const lpImpeachmentAmountAccess = getFieldAccess(fieldPermissions, 'accidents.lpimpeachmentamount', canEdit);

    return (
        <>
            <H5>Kaza Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="licensePlate">
                            Plaka {licensePlateAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="licensePlate"
                            name="licensePlate"
                            value={formData.licensePlate || ''}
                            disabled={licensePlateAccess.disabled}
                            onChange={(e) => {
                                const formattedValue = normalizeFieldValue('licensePlate', e.target.value);
                                setFormData(prev => ({
                                    ...prev,
                                    licensePlate: formattedValue || null
                                }));
                            }}
                            invalid={!!errors.licensePlate || !!(formData.licensePlate && !validateLicensePlate(formData.licensePlate))}
                            placeholder="Örn: 34 AB 123"
                        />
                        <FormFeedback style={{ display: !!errors.licensePlate ? 'block' : 'none' }}>
                            {errors.licensePlate || (formData.licensePlate && !validateLicensePlate(formData.licensePlate) ? 'Geçerli bir plaka giriniz (Örn: 34 AB 123).' : 'Plaka formatı otomatik düzeltilir.')}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="chassisNumber">
                            Şase Numarası {chassisNumberAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="chassisNumber"
                            name="chassisNumber"
                            value={formData.chassisNumber || ''}
                            disabled={chassisNumberAccess.disabled}
                            onChange={(e) => {
                                const normalized = normalizeFieldValue('chassisNumber', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'chassisNumber',
                                        value: normalized || null
                                    }
                                });
                            }}
                            maxLength={17}
                            pattern="[A-Z0-9]{17}"
                            invalid={!!errors.chassisNumber || (formData.chassisNumber && formData.chassisNumber.length !== 17)}
                        />
                        <FormFeedback>
                            {errors.chassisNumber || (formData.chassisNumber && formData.chassisNumber.length !== 17 ? 'Şase Numarası 17 karakter olmalıdır' : '')}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label for="carBrand">
                            Araç Markası {carBrandAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="carBrand"
                            name="carBrand"
                            value={formData.carBrand || ''}
                            disabled={carBrandAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('carBrand', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'carBrand',
                                        value: value || null
                                    }
                                });
                            }}
                            invalid={!!errors.carBrand}
                        />
                        <FormFeedback>{errors.carBrand}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="carModel">
                            Araç Modeli {carModelAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="carModel"
                            name="carModel"
                            value={formData.carModel || ''}
                            disabled={carModelAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('carModel', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'carModel',
                                        value: value || null
                                    }
                                });
                            }}
                            invalid={!!errors.carModel}
                        />
                        <FormFeedback>{errors.carModel}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="carYear">
                            Araç Yılı {carYearAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="carYear"
                            name="carYear"
                            value={formData.carYear || ''}
                            disabled={carYearAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('carYear', e.target.value);
                                const currentYear = new Date().getFullYear();
                                if (!value || parseInt(value) <= currentYear) {
                                    handleInputChange({
                                        target: {
                                            name: 'carYear',
                                            value: value || null
                                        }
                                    });
                                }
                            }}
                            maxLength={4}
                            placeholder={`1900 - ${new Date().getFullYear()}`}
                            invalid={!!errors.carYear || (formData.carYear && (parseInt(formData.carYear) > new Date().getFullYear() || parseInt(formData.carYear) < 1900))}
                        />
                        <FormFeedback>
                            {errors.carYear || (formData.carYear && (parseInt(formData.carYear) > new Date().getFullYear() || parseInt(formData.carYear) < 1900) ? `Araç yılı 1900 ile ${new Date().getFullYear()} arasında olmalıdır` : '')}
                        </FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <Label for="customerContractRate">
                    Sözleşme Oranı (%) {customerContractRateAccess.required && <span className="text-danger">*</span>}
                </Label>
                <Input
                    type="text"
                    id="customerContractRate"
                    name="customerContractRate"
                    value={displayDecimal(formData.customerContractRate || '')}
                    disabled={customerContractRateAccess.disabled}
                    onChange={(e) => {
                        const value = normalizeFieldValue('customerContractRate', e.target.value);
                        handleInputChange({
                            target: {
                                name: 'customerContractRate',
                                value: value || null
                            }
                        });
                    }}
                    placeholder="0-100"
                    invalid={!!errors.customerContractRate}
                />
                <FormFeedback>{errors.customerContractRate}</FormFeedback>
            </FormGroup>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="customerEnforcementAttorneyFeeShareRate">
                            İcra Vekalet Hakediş Oranı (%) {customerEnforcementShareAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="customerEnforcementAttorneyFeeShareRate"
                            name="customerEnforcementAttorneyFeeShareRate"
                            value={displayDecimal(formData.customerEnforcementAttorneyFeeShareRate || '')}
                            disabled={customerEnforcementShareAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('customerEnforcementAttorneyFeeShareRate', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'customerEnforcementAttorneyFeeShareRate',
                                        value: value || null
                                    }
                                });
                            }}
                            placeholder="0-100"
                            invalid={!!errors.customerEnforcementAttorneyFeeShareRate}
                        />
                        <FormFeedback>{errors.customerEnforcementAttorneyFeeShareRate}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="customerJudgmentAttorneyFeeShareRate">
                            İlam Vekalet Hakediş Oranı (%) {customerJudgmentShareAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="customerJudgmentAttorneyFeeShareRate"
                            name="customerJudgmentAttorneyFeeShareRate"
                            value={displayDecimal(formData.customerJudgmentAttorneyFeeShareRate || '')}
                            disabled={customerJudgmentShareAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('customerJudgmentAttorneyFeeShareRate', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'customerJudgmentAttorneyFeeShareRate',
                                        value: value || null
                                    }
                                });
                            }}
                            placeholder="0-100"
                            invalid={!!errors.customerJudgmentAttorneyFeeShareRate}
                        />
                        <FormFeedback>{errors.customerJudgmentAttorneyFeeShareRate}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <FormGroup>
                <Label className="d-block mb-2">
                    Müşteriye Faiz Ödenecek mi? {customerPayInterestAccess.required && <span className="text-danger">*</span>}
                </Label>
                <div className="d-flex align-items-center">
                    <div className="radio radio-success me-3 d-flex align-items-center">
                        <Input
                            id="customerPayInterestToCustomerTrue"
                            type="radio"
                            name="customerPayInterestToCustomer"
                            checked={formData.customerPayInterestToCustomer === true}
                            disabled={customerPayInterestAccess.disabled}
                            onChange={() => handleInputChange({
                                target: {
                                    name: 'customerPayInterestToCustomer',
                                    value: true
                                }
                            })}
                        />
                        <Label for="customerPayInterestToCustomerTrue" className="ms-1 mb-0">
                            Evet
                        </Label>
                    </div>
                    <div className="radio radio-danger d-flex align-items-center">
                        <Input
                            id="customerPayInterestToCustomerFalse"
                            type="radio"
                            name="customerPayInterestToCustomer"
                            checked={formData.customerPayInterestToCustomer === false}
                            disabled={customerPayInterestAccess.disabled}
                            onChange={() => handleInputChange({
                                target: {
                                    name: 'customerPayInterestToCustomer',
                                    value: false
                                }
                            })}
                        />
                        <Label for="customerPayInterestToCustomerFalse" className="ms-1 mb-0">
                            Hayır
                        </Label>
                    </div>
                </div>
            </FormGroup>

            <div style={{ height: "20px" }}></div>

            <FormGroup className="d-flex align-items-center mb-4">
                <div className="d-flex">
                    <div className="radio radio-primary me-3 d-flex align-items-center">
                        <Input
                            id="isTrialDocumentTrue"
                            type="radio"
                            name="documentType"
                            value="trial"
                            checked={formData.isTrialDocument === "yes"}
                            disabled={trialDocAccess.disabled}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isTrialDocument: "yes",
                                    isWorkDocument: "no",
                                }))
                            }
                            invalid={!!errors.documentType}
                        />
                        <Label for="isTrialDocumentTrue" className="ms-1 mb-0">
                            Deneme Dosyası
                        </Label>
                    </div>
                    <div className="radio radio-primary me-3 d-flex align-items-center">
                        <Input
                            id="isWorkDocumentTrue"
                            type="radio"
                            name="documentType"
                            value="work"
                            checked={formData.isWorkDocument === "yes" || (formData.isTrialDocument !== "yes" && formData.isWorkDocument !== "yes")}
                            disabled={workDocAccess.disabled}
                            onChange={() =>
                                setFormData((prevFormData) => ({
                                    ...prevFormData,
                                    isTrialDocument: "no",
                                    isWorkDocument: "yes",
                                }))
                            }
                            invalid={!!errors.documentType}
                        />
                        <Label for="isWorkDocumentTrue" className="ms-1 mb-0">
                            İş Dosyası
                        </Label>
                    </div>
                    <FormFeedback>{errors.documentType}</FormFeedback>
                </div>
            </FormGroup>

            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="hasCarSoldBeforeRadio" className="text-nowrap mb-0">
                            Önceden Araç Satış Var Mı? {carSoldAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="hasCarSoldBeforeTrue"
                                    type="radio"
                                    name="hasCarSoldBefore"
                                    value="yes"
                                    checked={formData.hasCarSoldBefore === "yes"}
                                    disabled={carSoldAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            hasCarSoldBefore: "yes",
                                        }))
                                    }
                                    invalid={!!errors.hasCarSoldBefore}
                                />
                                <Label for="hasCarSoldBeforeTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="hasCarSoldBeforeFalse"
                                    type="radio"
                                    name="hasCarSoldBefore"
                                    value="no"
                                    checked={formData.hasCarSoldBefore === "no" || formData.hasCarSoldBefore === null}
                                    disabled={carSoldAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            hasCarSoldBefore: "no",
                                        }))
                                    }
                                    invalid={!!errors.hasCarSoldBefore}
                                />
                                <Label for="hasCarSoldBeforeFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.hasCarSoldBefore ? 'block' : 'none' }}>{errors.hasCarSoldBefore}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="isThereLovClaimRadio" className="text-nowrap mb-0">
                            Önceden Değer Kaybı Başvurusu Var Mı? {lovClaimAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="isThereLovClaimTrue"
                                    type="radio"
                                    name="isThereLovClaim"
                                    value="yes"
                                    checked={formData.isThereLovClaim === "yes"}
                                    disabled={lovClaimAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            isThereLovClaim: "yes",
                                        }))
                                    }
                                    invalid={!!errors.isThereLovClaim}
                                />
                                <Label for="isThereLovClaimTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="isThereLovClaimFalse"
                                    type="radio"
                                    name="isThereLovClaim"
                                    value="no"
                                    checked={formData.isThereLovClaim === "no" || formData.isThereLovClaim === null}
                                    disabled={lovClaimAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            isThereLovClaim: "no",
                                            lovClaimAmount: null,
                                        }))
                                    }
                                    invalid={!!errors.isThereLovClaim}
                                />
                                <Label for="isThereLovClaimFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.isThereLovClaim ? 'block' : 'none' }}>{errors.isThereLovClaim}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>
            {formData.isThereLovClaim === "yes" && (
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="lovClaimAmount">
                                Önceden Alınan Ödeme Tutarı {lovClaimAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="lovClaimAmount"
                                name="lovClaimAmount"
                                value={displayDecimal(formData.lovClaimAmount ?? '')}
                                disabled={lovClaimAmountAccess.disabled}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('lovClaimAmount', e.target.value);

                                    handleInputChange({
                                        target: {
                                            name: 'lovClaimAmount',
                                            value: value || ''
                                        }
                                    });
                                }}
                                placeholder="Örn: 1000,50"
                                invalid={!!errors.lovClaimAmount}
                            />
                            <FormFeedback>{errors.lovClaimAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}
            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="lpImpeachmentStatusRadio" className="text-nowrap mb-0">
                            Plakada Azil Var Mı? {lpImpeachmentStatusAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="lpImpeachmentStatusTrue"
                                    type="radio"
                                    name="lpImpeachmentStatus"
                                    value="yes"
                                    checked={formData.lpImpeachmentStatus === true || formData.lpImpeachmentStatus === "yes"}
                                    disabled={lpImpeachmentStatusAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            lpImpeachmentStatus: "yes",
                                        }))
                                    }
                                    invalid={!!errors.lpImpeachmentStatus}
                                />
                                <Label for="lpImpeachmentStatusTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="lpImpeachmentStatusFalse"
                                    type="radio"
                                    name="lpImpeachmentStatus"
                                    value="no"
                                    checked={formData.lpImpeachmentStatus === false || formData.lpImpeachmentStatus === null || formData.lpImpeachmentStatus === undefined || formData.lpImpeachmentStatus === "no"}
                                    disabled={lpImpeachmentStatusAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            lpImpeachmentStatus: "no",
                                            lpImpeachmentAmount: null,
                                        }))
                                    }
                                    invalid={!!errors.lpImpeachmentStatus}
                                />
                                <Label for="lpImpeachmentStatusFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.lpImpeachmentStatus ? 'block' : 'none' }}>{errors.lpImpeachmentStatus}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>
            {(formData.lpImpeachmentStatus === "yes" || formData.lpImpeachmentStatus === true) && (
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="lpImpeachmentAmount">
                                Plaka Azil Tutarı {lpImpeachmentAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="lpImpeachmentAmount"
                                name="lpImpeachmentAmount"
                                value={displayDecimal(formData.lpImpeachmentAmount ?? '')}
                                disabled={lpImpeachmentAmountAccess.disabled}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('lpImpeachmentAmount', e.target.value);

                                    handleInputChange({
                                        target: {
                                            name: 'lpImpeachmentAmount',
                                            value: value || ''
                                        }
                                    });
                                }}
                                placeholder="Örn: 1000,50"
                                invalid={!!errors.lpImpeachmentAmount}
                            />
                            <FormFeedback>{errors.lpImpeachmentAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="shouldPayNotaryFeeRadio" className="text-nowrap mb-0">
                            Noter Ücreti Ödenecek Mi? {notaryFeeAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="shouldPayNotaryFeeTrue"
                                    type="radio"
                                    name="shouldPayNotaryFee"
                                    value="yes"
                                    checked={formData.shouldPayNotaryFee === "yes"}
                                    disabled={notaryFeeAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            shouldPayNotaryFee: "yes",
                                        }))
                                    }
                                    invalid={!!errors.shouldPayNotaryFee}
                                />
                                <Label for="shouldPayNotaryFeeTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="shouldPayNotaryFeeFalse"
                                    type="radio"
                                    name="shouldPayNotaryFee"
                                    value="no"
                                    checked={formData.shouldPayNotaryFee === "no" || formData.shouldPayNotaryFee === null}
                                    disabled={notaryFeeAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            shouldPayNotaryFee: "no",
                                            notaryFeeAmount: null,
                                        }))
                                    }
                                    invalid={!!errors.shouldPayNotaryFee}
                                />
                                <Label for="shouldPayNotaryFeeFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.shouldPayNotaryFee ? 'block' : 'none' }}>{errors.shouldPayNotaryFee}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            {formData.shouldPayNotaryFee === "yes" && (
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="notaryFeeAmount">
                                Notere Ödenecek Tutar {notaryAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="notaryFeeAmount"
                                name="notaryFeeAmount"
                                value={displayDecimal(formData.notaryFeeAmount ?? '')}
                                disabled={notaryAmountAccess.disabled}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('notaryFeeAmount', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'notaryFeeAmount',
                                            value: value || ''
                                        }
                                    });
                                }}
                                placeholder="Örn: 1000,50"
                                invalid={!!errors.notaryFeeAmount}
                            />
                            <FormFeedback>{errors.notaryFeeAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            <FormGroup className="mb-3">
                <Row className="align-items-center">
                    <Col xl={4} md={6}>
                        <Label for="willLorBeWorkedRadio" className="text-nowrap mb-0">
                            Hak Mahrumiyeti Çalışılacak Mı? {willLorAccess.required && <span className="text-danger">*</span>}
                        </Label>
                    </Col>
                    <Col xl={8} md={6} className='mt-md-0 mt-2'>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="willLorBeWorkedTrue"
                                    type="radio"
                                    name="willLorBeWorked"
                                    value="yes"
                                    checked={formData.willLorBeWorked === "yes"}
                                    disabled={willLorAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            willLorBeWorked: "yes",
                                        }))
                                    }
                                    invalid={!!errors.willLorBeWorked}
                                />
                                <Label for="willLorBeWorkedTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="willLorBeWorkedFalse"
                                    type="radio"
                                    name="willLorBeWorked"
                                    value="no"
                                    checked={formData.willLorBeWorked === "no" || formData.willLorBeWorked === null}
                                    disabled={willLorAccess.disabled}
                                    onChange={() =>
                                        setFormData((prevFormData) => ({
                                            ...prevFormData,
                                            willLorBeWorked: "no",
                                        }))
                                    }
                                    invalid={!!errors.willLorBeWorked}
                                />
                                <Label for="willLorBeWorkedFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                        <FormFeedback style={{ display: errors.willLorBeWorked ? 'block' : 'none' }}>{errors.willLorBeWorked}</FormFeedback>
                    </Col>
                </Row>
            </FormGroup>

            <Row>
                <Col md={formData.willLorBeWorked === "yes" ? 3 : 4}>
                    <FormGroup>
                        <Label for="estimatedDepreciationAmount">
                            Değer Kaybı Tutarı {estDepreciationAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="estimatedDepreciationAmount"
                            name="estimatedDepreciationAmount"
                            value={displayDecimal(formData.estimatedDepreciationAmount ?? '')}
                            disabled={estDepreciationAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('estimatedDepreciationAmount', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'estimatedDepreciationAmount',
                                        value: value || ''
                                    }
                                });
                            }}
                            placeholder="Örn: 1000,50"
                            invalid={!!errors.estimatedDepreciationAmount}
                        />
                        <FormFeedback>{errors.estimatedDepreciationAmount}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={formData.willLorBeWorked === "yes" ? 3 : 4}>
                    <FormGroup>
                        <Label for="damageAmount">
                            Hasar Tutarı {damageAmountAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="damageAmount"
                            name="damageAmount"
                            value={displayDecimal(formData.damageAmount ?? '')}
                            disabled={damageAmountAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('damageAmount', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'damageAmount',
                                        value: value || ''
                                    }
                                });
                            }}
                            placeholder="Örn: 1000,50"
                            invalid={!!errors.damageAmount}
                        />
                        <FormFeedback>{errors.damageAmount}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={formData.willLorBeWorked === "yes" ? 3 : 4}>
                    <FormGroup>
                        <Label for="damageDifferenceAmount">
                            Hasar Farkı Tutarı {damageDiffAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="damageDifferenceAmount"
                            name="damageDifferenceAmount"
                            value={displayDecimal(formData.damageDifferenceAmount ?? '')}
                            disabled={damageDiffAccess.disabled}
                            onChange={(e) => {
                                const value = normalizeFieldValue('damageDifferenceAmount', e.target.value);
                                handleInputChange({
                                    target: {
                                        name: 'damageDifferenceAmount',
                                        value: value || ''
                                    }
                                });
                            }}
                            placeholder="Örn: 1000,50"
                            invalid={!!errors.damageDifferenceAmount}
                        />
                        <FormFeedback>{errors.damageDifferenceAmount}</FormFeedback>
                    </FormGroup>
                </Col>
                {formData.willLorBeWorked === "yes" && (
                    <Col md={3}>
                        <FormGroup>
                            <Label for="lorAmount">
                                Hak Mahrumiyeti Tutarı {lorAmountAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="lorAmount"
                                name="lorAmount"
                                value={displayDecimal(formData.lorAmount ?? '')}
                                disabled={lorAmountAccess.disabled}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('lorAmount', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'lorAmount',
                                            value: value || ''
                                        }
                                    });
                                }}
                                placeholder="Örn: 1000,50"
                                invalid={!!errors.lorAmount}
                            />
                            <FormFeedback>{errors.lorAmount}</FormFeedback>
                        </FormGroup>
                    </Col>
                )}
            </Row>

            <FormGroup>
                <Label for="accidentDate">
                    Kaza Tarihi {accidentDateAccess.required && <span className="text-danger">*</span>}
                </Label>
                <Input
                    type="datetime-local"
                    id="accidentDate"
                    name="accidentDate"
                    value={formData.accidentDate instanceof Date && !isNaN(formData.accidentDate)
                        ? formData.accidentDate.toISOString().slice(0, 16)
                        : ''}
                    disabled={accidentDateAccess.disabled}
                    onChange={(e) => {
                        const value = e.target.value ? new Date(e.target.value) : null;
                        handleInputChange({
                            target: {
                                name: 'accidentDate',
                                value
                            }
                        });
                    }}
                    invalid={!!errors.accidentDate}
                />
                <FormFeedback>{errors.accidentDate}</FormFeedback>
            </FormGroup>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="ourFaultRate">
                            Müvekkil Kusur Oranı {ourFaultRateAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <div className="input-group">
                            <Input
                                type="text"
                                id="ourFaultRate"
                                name="ourFaultRate"
                                value={displayDecimal(formData.ourFaultRate || '')}
                                disabled={ourFaultRateAccess.disabled}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('ourFaultRate', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'ourFaultRate',
                                            value: value || null 
                                        }
                                    });
                                    const counterValue = value ? (100 - parseFloat(value)).toString() : '';
                                    handleInputChange({
                                        target: {
                                            name: 'counterpartyFaultRate',
                                            value: counterValue || null 
                                        }
                                    });
                                }}
                                placeholder="0-100"
                                invalid={!!errors.ourFaultRate}
                            />
                            <span className="input-group-text">%</span>
                            <FormFeedback>{errors.ourFaultRate}</FormFeedback>
                        </div>
                    </FormGroup>
                </Col>
            </Row>
        </>
    );
};

AccidentInfoForm.propTypes = {
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    setFormData: PropTypes.func.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    fieldPermissions: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default AccidentInfoForm;
